/* eslint-disable jsx-a11y/no-noninteractive-element-interactions,
                  jsx-a11y/no-noninteractive-tabindex --
   The crop stage is an application widget: focusable, and operable from the
   keyboard with arrows to pan and +/- to zoom. The rule judges by role alone
   and cannot see that. */
import { useCallback, useEffect, useRef, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  INITIAL_TRANSFORM,
  MAX_ZOOM,
  MIN_ZOOM,
  applyPan,
  applyZoom,
  clampOffset,
  cropRect,
  scaleFor,
  type CropRect,
  type CropTransform,
  type Size,
} from "./cropGeometry";

interface CropImageDialogProps {
  open: boolean;
  /** Object URL of the image being framed. */
  imageUrl: string | null;
  naturalSize: Size | null;
  /** Reopening from Adjust crop starts where the operator left off. */
  initialTransform?: CropTransform;
  /** Width ÷ height the frame is locked to. */
  aspectRatio?: number;
  title?: string;
  /** Shown under the title, e.g. what the crop will be saved as. */
  description?: string;
  saveLabel?: string;
  /** Disables Save while the caller is exporting or uploading. */
  isSaving?: boolean;
  onCancel: () => void;
  onSave: (rect: CropRect, transform: CropTransform) => void;
}

const KEY_NUDGE = 8;
const ZOOM_STEP = 0.1;

export function CropImageDialog({
  open,
  imageUrl,
  naturalSize,
  initialTransform,
  aspectRatio = 2,
  title = "Crop image",
  description,
  saveLabel = "Save",
  isSaving = false,
  onCancel,
  onSave,
}: CropImageDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Esc and the ✕ both close without saving.
        if (!next) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {open && imageUrl && naturalSize && (
          // Keyed so each image starts from its own transform rather than
          // inheriting the previous one's.
          <CropStage
            key={imageUrl}
            imageUrl={imageUrl}
            naturalSize={naturalSize}
            initialTransform={initialTransform}
            aspectRatio={aspectRatio}
            saveLabel={saveLabel}
            isSaving={isSaving}
            onCancel={onCancel}
            onSave={onSave}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CropStage({
  imageUrl,
  naturalSize,
  initialTransform,
  aspectRatio,
  saveLabel,
  isSaving,
  onCancel,
  onSave,
}: {
  imageUrl: string;
  naturalSize: Size;
  initialTransform?: CropTransform;
  aspectRatio: number;
  saveLabel: string;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (rect: CropRect, transform: CropTransform) => void;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [frame, setFrame] = useState<Size | null>(null);
  const [transform, setTransform] = useState<CropTransform>(
    initialTransform ?? INITIAL_TRANSFORM,
  );
  const dragRef = useRef<{ pointerId: number; x: number; y: number } | null>(
    null,
  );

  // The offsets are in the frame's own pixels, so a resize changes what they
  // mean; re-clamp rather than letting an edge drift inside the frame.
  useEffect(() => {
    const element = frameRef.current;
    if (!element) return;
    const measure = () => {
      const next = {
        width: element.clientWidth,
        height: element.clientHeight,
      };
      if (next.width <= 0) return;
      setFrame(next);
      setTransform((current) => ({
        zoom: current.zoom,
        offset: clampOffset(current.offset, naturalSize, next, current.zoom),
      }));
    };
    measure();
    // Measuring once is enough to crop; the observer only keeps the clamp
    // honest if the dialog is resized, so its absence must not break the
    // dialog outright.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [naturalSize]);

  const pan = useCallback(
    (dx: number, dy: number) => {
      if (!frame) return;
      setTransform((current) =>
        applyPan(current, { x: dx, y: dy }, naturalSize, frame),
      );
    },
    [frame, naturalSize],
  );

  const zoomTo = useCallback(
    (next: number) => {
      if (!frame) return;
      setTransform((current) => applyZoom(current, next, naturalSize, frame));
    },
    [frame, naturalSize],
  );

  const scale = frame ? scaleFor(naturalSize, frame, transform.zoom) : 1;

  return (
    <div className="flex flex-col gap-3">
      <div
        role="application"
        aria-label="Drag the image to position it in the frame"
        tabIndex={0}
        onKeyDown={(e) => {
          const moves: Record<string, [number, number]> = {
            ArrowLeft: [KEY_NUDGE, 0],
            ArrowRight: [-KEY_NUDGE, 0],
            ArrowUp: [0, KEY_NUDGE],
            ArrowDown: [0, -KEY_NUDGE],
          };
          const move = moves[e.key];
          if (move) {
            e.preventDefault();
            pan(move[0], move[1]);
            return;
          }
          if (e.key === "+" || e.key === "=") {
            e.preventDefault();
            zoomTo(transform.zoom + ZOOM_STEP);
          }
          if (e.key === "-" || e.key === "_") {
            e.preventDefault();
            zoomTo(transform.zoom - ZOOM_STEP);
          }
        }}
        onWheel={(e) => zoomTo(transform.zoom - e.deltaY * 0.002)}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          dragRef.current = {
            pointerId: e.pointerId,
            x: e.clientX,
            y: e.clientY,
          };
        }}
        onPointerMove={(e) => {
          const drag = dragRef.current;
          if (!drag || drag.pointerId !== e.pointerId) return;
          pan(e.clientX - drag.x, e.clientY - drag.y);
          dragRef.current = {
            pointerId: e.pointerId,
            x: e.clientX,
            y: e.clientY,
          };
        }}
        onPointerUp={() => {
          dragRef.current = null;
        }}
        // A cancelled gesture — a second finger, the browser taking over to
        // scroll, the window losing focus — must not leave the drag latched.
        onPointerCancel={() => {
          dragRef.current = null;
        }}
        onLostPointerCapture={() => {
          dragRef.current = null;
        }}
        onBlur={() => {
          dragRef.current = null;
        }}
        className="relative cursor-grab touch-none overflow-hidden rounded-xl bg-ink/90 p-6 active:cursor-grabbing focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        <div
          ref={frameRef}
          style={{ aspectRatio }}
          className="relative mx-auto w-full shadow-[0_0_0_9999px_rgba(0,0,0,0.55)]"
        >
          {/* The image sits behind the frame, centred on it and scaled to
              cover; the ring shadow above dims everything outside. */}
          <img
            src={imageUrl}
            alt=""
            draggable={false}
            aria-hidden
            style={{
              width: naturalSize.width * scale,
              height: naturalSize.height * scale,
              transform: `translate(-50%, -50%) translate(${transform.offset.x}px, ${transform.offset.y}px)`,
            }}
            className="pointer-events-none absolute left-1/2 top-1/2 max-w-none"
          />
          {/* Rule-of-thirds guides, drawn over the framed area only. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 z-10 border border-white/70"
          >
            <div className="absolute inset-y-0 left-1/3 w-px bg-white/40" />
            <div className="absolute inset-y-0 left-2/3 w-px bg-white/40" />
            <div className="absolute inset-x-0 top-1/3 h-px bg-white/40" />
            <div className="absolute inset-x-0 top-2/3 h-px bg-white/40" />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label="Zoom out"
          onClick={() => zoomTo(transform.zoom - ZOOM_STEP)}
          disabled={transform.zoom <= MIN_ZOOM}
          className="text-text-muted disabled:opacity-40"
        >
          <Minus className="size-4" />
        </button>
        <input
          type="range"
          aria-label="Zoom"
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step={0.01}
          value={transform.zoom}
          onChange={(e) => zoomTo(Number(e.target.value))}
          className="h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-muted accent-primary"
        />
        <button
          type="button"
          aria-label="Zoom in"
          onClick={() => zoomTo(transform.zoom + ZOOM_STEP)}
          disabled={transform.zoom >= MAX_ZOOM}
          className="text-text-muted disabled:opacity-40"
        >
          <Plus className="size-4" />
        </button>
      </div>
      <p className="mulish text-center text-xs font-semibold text-text-muted">
        Drag the image to position it in the frame
      </p>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="rounded-lg"
        >
          Cancel
        </Button>
        <Button
          type="button"
          disabled={!frame || isSaving}
          onClick={() => {
            if (!frame) return;
            onSave(cropRect(transform, naturalSize, frame), transform);
          }}
          className="rounded-lg"
        >
          {saveLabel}
        </Button>
      </DialogFooter>
    </div>
  );
}
